<script lang="ts" setup>
import { AtSymbolIcon, ChevronDownIcon, PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { router } from '@inertiajs/vue3';
import { Card } from '@vue-interface/card';
import { CheckboxField } from '@vue-interface/checkbox-field';
import { InputField } from '@vue-interface/input-field';
import { Confirm, Modal } from '@vue-interface/modal';
import { TagField } from '@vue-interface/tag-field';
import { useForm } from 'laravel-precognition-vue-inertia';
import { vMaska } from 'maska';
import type { Agency, Subscriber, SubscriberList } from 'types';
import { ref } from 'vue';
import { route } from '../composables/routes';
import FormField from './FormField.vue';
import FormLayout from './FormLayout.vue';
import StateField from './StateField.vue';

const props = defineProps<{
    subscriber?: Subscriber,
    agency?: Agency,
    list?: SubscriberList,
    url: string,
    states: Record<string, string>
}>();

const method = props.subscriber?.id ? 'put' : 'post';

const customFields = props.subscriber?.subscriber_field?.fields ?? {};

const form = useForm(method, props.url, {
    first: props.subscriber?.first ?? undefined,
    last: props.subscriber?.last ?? undefined,
    email: props.subscriber?.email ?? undefined,
    zip: props.subscriber?.zip ?? undefined,
    street: props.subscriber?.street ?? undefined,
    city: props.subscriber?.city ?? undefined,
    state: props.subscriber?.state ?? undefined,
    phone: props.subscriber?.phone ?? undefined,
    tags: props.subscriber?.tags ?? [] as string[],
    custom_fields: { ...customFields } as Record<string, string>,
});

const emailChangeData = ref({
    email: props.subscriber?.email || '',
    remove_from_kill_list: false
});

const emailChangeProcessing = ref(false);
const emailChangeErrors = ref<Record<string,string>>();

// Custom fields state
const customFieldsOpen = ref(false);

const addFieldModal = ref<InstanceType<typeof Modal> | null>(null);
const addFieldName = ref('');
const addFieldError = ref('');

const editFieldModal = ref<InstanceType<typeof Modal> | null>(null);
const editFieldOriginalKey = ref('');
const editFieldName = ref('');
const editFieldError = ref('');

function onSubmit() {
    form.submit();
}

function onConfirmEmailChange(event: MouseEvent, button: HTMLButtonElement, context: { close: Function }) {
    if(!props.agency || !props.list || !props.subscriber) return;

    button.disabled = true;
    emailChangeProcessing.value = true;
    emailChangeErrors.value = undefined;

    router.patch(route('subscribers.change-email', {
        agency: props.agency.slug,
        list: props.list.slug,
        subscriber: props.subscriber.id
    }), emailChangeData.value, {
        onError(errors) {
            emailChangeErrors.value = errors;
            button.disabled = false;
            emailChangeProcessing.value = false;
        }
    });
}

function toFieldKey(name: string): string {
    return name.trim().toLowerCase().replace(/\s+/g, '_');
}

function openAddField() {
    addFieldName.value = '';
    addFieldError.value = '';
    addFieldModal.value?.open();
}

function submitAddField() {
    const key = toFieldKey(addFieldName.value);

    if(!key) {
        addFieldError.value = 'Field name is required.';
        return;
    }

    if(key in form.custom_fields) {
        addFieldError.value = 'A field with this name already exists.';
        return;
    }

    form.custom_fields[key] = '';
    addFieldModal.value?.close();
}

function openEditField(key: string) {
    editFieldOriginalKey.value = key;
    editFieldName.value = key.replaceAll('_', ' ');
    editFieldError.value = '';
    editFieldModal.value?.open();
}

function submitEditField() {
    const newKey = toFieldKey(editFieldName.value);

    if(!newKey) {
        editFieldError.value = 'Field name is required.';
        return;
    }

    if(newKey !== editFieldOriginalKey.value && newKey in form.custom_fields) {
        editFieldError.value = 'A field with this name already exists.';
        return;
    }

    if(newKey !== editFieldOriginalKey.value) {
        const value = form.custom_fields[editFieldOriginalKey.value];
        delete form.custom_fields[editFieldOriginalKey.value];
        form.custom_fields[newKey] = value;
    }

    editFieldModal.value?.close();
}

function deleteField(key: string) {
    if(!confirm(`Are you sure you want to delete the "${key.replaceAll('_', ' ')}" field?`)) {
        return;
    }

    delete form.custom_fields[key];
}
</script>

<template>
    <Confirm
        trigger="#change-email"
        title="Change Email Address"
        close-button
        :colors="{
            info: 'bg-rose-100 text-rose-400',
        }"
        :icon="AtSymbolIcon"
        confirm-label="Change Email"
        @confirm="onConfirmEmailChange">
        <div class="space-y-4">
            <p class="text-sm text-neutral-600 dark:text-neutral-400">
                If the new email already exists within this organization, the subscribers will be merged.
            </p>

            <InputField
                v-model="emailChangeData.email"
                label="New Email Address"
                type="email"
                placeholder="newemail@example.com"
                :error="emailChangeErrors?.email"
                required />

            <CheckboxField
                v-model="emailChangeData.remove_from_kill_list"
                label="Remove From Global Suppressions "
                :error="emailChangeErrors?.remove_from_kill_list" />

            <p class="text-xs text-neutral-500 dark:text-neutral-400">
                If the new email is on the global suppression list and this option is not checked, this change will be blocked.
            </p>
        </div>
    </Confirm>

    <!-- Add field modal -->
    <Modal
        ref="addFieldModal"
        title="Add Custom Field"
        close-button
        :icon="false"
        :footer="false">
        <form
            class="flex flex-col"
            @submit.prevent="submitAddField">
            <FormField
                label="Field Name"
                block
                required>
                <input
                    v-model="addFieldName"
                    type="text"
                    class="form-control"
                    maxlength="64"
                    placeholder="e.g. Company Name"
                    required>
                <p
                    v-if="addFieldError"
                    class="mt-1 text-sm text-rose-500">
                    {{ addFieldError }}
                </p>
            </FormField>

            <div class="flex justify-end gap-2 pt-2">
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="addFieldModal?.close()">
                    Cancel
                </button>
                <button
                    type="submit"
                    class="btn btn-primary">
                    Add Field
                </button>
            </div>
        </form>
    </Modal>

    <!-- Edit field modal -->
    <Modal
        ref="editFieldModal"
        title="Rename Custom Field"
        close-button
        :icon="false"
        :footer="false">
        <form
            class="flex flex-col"
            @submit.prevent="submitEditField">
            <FormField
                label="Field Name"
                block
                required>
                <input
                    v-model="editFieldName"
                    type="text"
                    class="form-control"
                    maxlength="64"
                    required>
                <p
                    v-if="editFieldError"
                    class="mt-1 text-sm text-rose-500">
                    {{ editFieldError }}
                </p>
            </FormField>

            <div class="flex justify-end gap-2 pt-2">
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="editFieldModal?.close()">
                    Cancel
                </button>
                <button
                    type="submit"
                    class="btn btn-primary">
                    Rename
                </button>
            </div>
        </form>
    </Modal>

    <FormLayout
        :submit="onSubmit"
        :button-label="!subscriber?.id ? 'Submit' : 'Save'"
        :activity="form.processing"
        :disabled="form.processing">
        <Card>
            <FormField
                label="Email"
                class="flex items-center">
                <div class="flex items-center gap-2 w-full">
                    <InputField
                        id="email"
                        v-model="form.email"
                        name="email"
                        :disabled="!!subscriber?.id"
                        :error="form.errors.email"
                        class="flex-1" />
                    <button
                        v-if="subscriber?.id"
                        id="change-email"
                        type="button"
                        class="btn-primary btn capitalize flex items-center gap-x-1 whitespace-nowrap">
                        Edit Email
                    </button>
                </div>
            </FormField>
            <FormField
                label="First Name"
                class="flex items-center">
                <InputField
                    id="first"
                    v-model="form.first"
                    type="text"
                    name="first"
                    :error="form.errors.first" />
            </FormField>
            <FormField
                label="Last Name"
                class="flex items-center">
                <InputField
                    id="last"
                    v-model="form.last"
                    type="text"
                    name="last"
                    :error="form.errors.last" />
            </FormField>
            <FormField
                label="Street"
                class="flex items-center">
                <InputField
                    id="street"
                    v-model="form.street"
                    type="text"
                    name="street"
                    :error="form.errors.street" />
            </FormField>
            <FormField
                label="City"
                class="flex items-center">
                <InputField
                    id="city"
                    v-model="form.city"
                    type="text"
                    name="city"
                    :error="form.errors.city" />
            </FormField>
            <FormField
                label="State"
                class="flex items-center">
                <StateField
                    id="state"
                    v-model="form.state"
                    :states="states"
                    name="state"
                    :error="form.errors.state" />
            </FormField>
            <FormField
                label="Zip"
                class="flex items-center">
                <InputField
                    id="zip"
                    v-model="form.zip"
                    v-maska
                    data-maska="['#####', '#####-####']"
                    placeholder="12345-6789"
                    type="text"
                    name="zip"
                    :error="form.errors.zip" />
            </FormField>
            <FormField
                label="Phone"
                class="flex items-center">
                <InputField
                    id="phone"
                    v-model="form.phone"
                    v-maska
                    data-maska="(###) ###-####"
                    placeholder="(000) 000-0000"
                    type="text"
                    name="phone"
                    :error="form.errors.phone" />
            </FormField>
            <FormField
                label="Tags"
                description="Type the tag and hit the enter key to add the tag."
                class="flex items-center">
                <TagField
                    id="tags"
                    v-model="form.tags"
                    mode="tags"
                    :options="form.tags"
                    allow-custom
                    badge-class="badge-rose-500 cursor-pointer"
                    active-badge-color="badge-rose-500" />
            </FormField>
        </Card>

        <div
            v-if="subscriber?.id"
            class="mt-6">
            <button
                type="button"
                class="flex items-center gap-1.5 py-2 text-sm font-semibold text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors"
                @click="customFieldsOpen = !customFieldsOpen">
                <ChevronDownIcon
                    class="size-4 transition-transform"
                    :class="{ '-rotate-90': !customFieldsOpen }" />
                <span>Custom Fields</span>
            </button>

            <Card v-if="customFieldsOpen">
                <FormField
                    v-for="(value, key) in form.custom_fields"
                    :key="key"
                    class="group flex items-center">
                    <template #label>
                        <div class="flex items-center gap-2 capitalize">
                            <span>{{ String(key).replaceAll('_', ' ') }}</span>
                            <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    type="button"
                                    class="text-neutral-400 hover:text-blue-500 transition-colors"
                                    title="Rename field"
                                    @click="openEditField(String(key))">
                                    <PencilSquareIcon class="size-3.5" />
                                </button>
                                <button
                                    type="button"
                                    class="text-neutral-400 hover:text-rose-500 transition-colors"
                                    title="Delete field"
                                    @click="deleteField(String(key))">
                                    <TrashIcon class="size-3.5" />
                                </button>
                            </div>
                        </div>
                    </template>
                    <InputField
                        v-model="form.custom_fields[key]"
                        type="text"
                        :name="`custom_fields.${key}`"
                        :error="form.errors[`custom_fields.${key}`]" />
                </FormField>

                <div class="flex justify-end p-3">
                    <button
                        type="button"
                        class="btn btn-secondary flex items-center gap-1"
                        @click="openAddField">
                        <PlusIcon class="size-4" />
                        Add Field
                    </button>
                </div>
            </Card>
        </div>
    </FormLayout>
</template>
